export var TimeUnits;
(function (TimeUnits) {
    TimeUnits["MILLISECONDS"] = "ms";
    TimeUnits["SECONDS"] = "seconds";
    TimeUnits["MINUTES"] = "minutes";
    TimeUnits["HOURS"] = "hours";
    TimeUnits["DAYS"] = "days";
    TimeUnits["MONTHS"] = "months";
    TimeUnits["YEARS"] = "years";
})(TimeUnits || (TimeUnits = {}));
/** Default sorting order is lowest first */
export const sortBy = (arr, fn, options = { reverse: false }) => {
    const newArr = arr.slice();
    const lt = options.reverse ? 1 : -1;
    const gt = options.reverse ? -1 : 1;
    return newArr.sort((a, b) => {
        const aKey = fn(a);
        const bKey = fn(b);
        if (aKey < bKey) {
            return lt;
        }
        if (aKey > bKey) {
            return gt;
        }
        return 0;
    });
};
export const uniqueBy = (arr, key) => {
    const seen = new Set();
    return arr.filter((item) => {
        const k = key(item);
        if (seen.has(k)) {
            return false;
        }
        seen.add(k);
        return true;
    });
};
export const objectBy = (arr, fn, base = {}) => {
    let obj = base;
    for (const item of arr) {
        obj[fn(item)] = item;
    }
    return obj;
};
export const aggregate = (arr, fn, base = {}) => {
    for (const el of arr) {
        const key = fn(el);
        if (base[key] == null) {
            base[key] = [el];
        }
        else {
            base[key].push(el);
        }
    }
    return base;
};
export const mapAggregate = (arr, fn, base = new Map()) => {
    for (const el of arr) {
        const key = fn(el);
        const group = base.get(key);
        if (group === undefined) {
            base.set(key, [el]);
        }
        else {
            group.push(el);
        }
    }
    return base;
};
export const getOrCreate = (map, key, create) => {
    let value = map.get(key);
    if (value === undefined) {
        value = create();
        map.set(key, value);
    }
    return value;
};
export const pushTo = (lists, key, value) => {
    getOrCreate(lists, key, () => []).push(value);
};
// Adds to the running total of `key`.
export const addTo = (totals, key, amount) => totals.set(key, (totals.get(key) ?? 0) + amount);
export const counted = (arr, fn, base = {}) => {
    for (const el of arr) {
        const key = fn(el);
        if (base[key] == null) {
            base[key] = 1;
        }
        else {
            base[key] += 1;
        }
    }
    return base;
};
export const intersperse = (arr, element) => {
    let neu = [];
    for (let i = 0; i < arr.length; i++) {
        if (i !== 0) {
            neu.push(element);
        }
        neu.push(arr[i]);
    }
    return neu;
};
export const levenshtein = (s, t) => {
    if (!s.length) {
        return t.length;
    }
    if (!t.length) {
        return s.length;
    }
    const arr = [];
    for (let i = 0; i <= t.length; i++) {
        arr[i] = [i];
        for (let j = 1; j <= s.length; j++) {
            arr[i][j] =
                i === 0
                    ? j
                    : Math.min(arr[i - 1][j] + 1, arr[i][j - 1] + 1, arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1));
        }
    }
    return arr[t.length][s.length];
};
export const stringCompare = (refString, query) => {
    const snippets = window(refString, query);
    const scores = snippets.map((snippet, i, snippets) => {
        const base = levenshtein(snippet.toLowerCase(), query.toLowerCase());
        const startOfText = i == 0 || /^["'\- ]/.test(snippets[i - 1]) ? 0 : 0.5;
        const indexScore = i / (snippets.length + 1) / 2;
        return base + startOfText + indexScore;
    });
    const bestIndex = argMin(scores);
    return {
        score: scores[bestIndex],
        start: bestIndex,
        end: bestIndex + snippets[bestIndex].length,
        match: snippets[bestIndex],
    };
};
const window = (text, query) => {
    const snippets = [];
    const queryLength = query.length;
    const textLength = text.length;
    if (queryLength > textLength) {
        return [text];
    }
    for (let i = 0; i < textLength - queryLength + 1; i++) {
        snippets.push(text.substring(i, i + queryLength));
    }
    return snippets;
};
export const argMin = (arr) => {
    let minIndex = 0;
    for (let i = 1; i < arr.length; i++) {
        if (arr[i] < arr[minIndex]) {
            minIndex = i;
        }
    }
    return minIndex;
};
export function search(arr, getText, query, options = { returnMeta: false }) {
    const results = arr.map((item) => {
        const snippets = window(getText(item), query);
        const scores = snippets.map((snippet, i, snippets) => {
            const base = levenshtein(snippet.toLowerCase(), query.toLowerCase());
            const startOfText = i == 0 || /^["'\- ]/.test(snippets[i - 1]) ? 0 : 0.5;
            const indexScore = i / (snippets.length + 1) / 2;
            return base + startOfText + indexScore;
        });
        const bestIndex = argMin(scores);
        return {
            item,
            score: scores[bestIndex],
            start: bestIndex,
            end: bestIndex + snippets[bestIndex].length,
            match: snippets[bestIndex],
        };
    });
    const returned = results
        .filter((r) => r.score < 2)
        .sort((a, b) => a.score - b.score);
    return options.returnMeta ? returned : returned.map((x) => x.item);
}
// Use with filter to make typescript happy
export function notEmpty(value) {
    return value !== null && value !== undefined;
}
const rightMostDecimalSeperator = (str) => {
    for (let i = str.length - 1; i >= 0; i--) {
        if (str[i] === ',') {
            return ',';
        }
        else if (str[i] === '.') {
            return '.';
        }
    }
    return '.';
};
// Parses a number, using the right most , or . as the decimal separator
export const parseNum = (val, seperator) => {
    const sep = seperator ?? rightMostDecimalSeperator(val);
    const thousandsSep = sep === '.' ? ',' : '.';
    const transformed = val.split(thousandsSep).join('').split(',').join('.');
    return parseFloat(transformed);
};
const ids = {};
export const debounce = (key, slowAction, time) => {
    const id = ids[key];
    clearTimeout(id);
    ids[key] = setTimeout(() => {
        delete ids[key];
        slowAction();
    }, time);
};
export const cancelDebounce = (key) => {
    clearTimeout(ids[key]);
};
export const toMoney = (num, options = {}) => {
    if (options.allowFractionalAmounts) {
        return Math.round(num * 10000) / 100;
    }
    return Math.round(num * 100);
};
export const equal = (a, b) => {
    if (isObject(a) && isObject(b)) {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) {
            return false;
        }
        for (const key of aKeys) {
            if (!equal(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }
    return JSON.stringify(a) === JSON.stringify(b);
};
export const arrayIntersection = (arr1, arr2) => {
    const s = new Set(arr1);
    return arr2.filter((e) => s.has(e));
};
export const isObject = (obj) => {
    return obj !== null && typeof obj === 'object';
};
// Between two objects, only at depth 1
export const findUpdatedValues = (prev, curr) => {
    const keys = Array.from(new Set(Object.keys(prev).concat(Object.keys(curr))));
    const updated = {};
    for (const key of keys) {
        if (!equal(prev[key], curr[key])) {
            updated[key] = curr[key];
        }
    }
    return updated;
};
export const pick = (value, keys) => {
    const x = {};
    for (const key of keys) {
        x[key] = value[key];
    }
    return x;
};
export const removeUndefined = (obj) => {
    const newObj = { ...obj };
    for (const key in newObj) {
        if (newObj[key] === undefined) {
            delete newObj[key];
        }
        else if (Array.isArray(newObj[key])) {
            newObj[key] = newObj[key].map((x) => {
                if (typeof x === 'object') {
                    return removeUndefined(x);
                }
                return x;
            });
        }
        else if (typeof newObj[key] === 'object' && newObj[key] != null) {
            newObj[key] = removeUndefined(newObj[key]);
        }
    }
    return newObj;
};
export const toQuery = (val) => {
    const newObj = {};
    for (const key in val) {
        if (typeof val[key] === 'object') {
            newObj[key] = JSON.stringify(val[key]);
        }
        else if (typeof val[key] === 'undefined') {
            continue;
        }
        else {
            newObj[key] = `${val[key]}`;
        }
    }
    return `${new URLSearchParams(newObj)}`;
};
const capitalize = (string) => {
    if (string.length === 0) {
        return '';
    }
    return string[0].toUpperCase() + string.slice(1);
};
const snakeToCamel = (string) => {
    const [start, ...rest] = string.split('_');
    return start + rest.map(capitalize).join('');
};
export const toCamelCase = (t) => {
    if (Array.isArray(t)) {
        return t.map((x) => toCamelCase(x));
    }
    else if (t != null && t.constructor === Object) {
        return Object.fromEntries(Object.entries(t).map(([key, val]) => {
            const convVal = typeof val === 'object' ? toCamelCase(val) : val;
            return [snakeToCamel(key), convVal];
        }));
    }
    return t;
};
export const camelToSnake = (str) => {
    const chunks = [];
    let lastCut = 0;
    for (let i = 0; i < str.length; i++) {
        if (str[i] === str[i].toLocaleUpperCase()) {
            chunks.push(str.slice(lastCut, i));
            chunks.push(`_${str[i].toLocaleLowerCase()}`);
            lastCut = i + 1;
        }
    }
    chunks.push(str.slice(lastCut, str.length));
    return chunks.join('');
};
export const toSnakeCase = (t) => {
    if (Array.isArray(t)) {
        return t.map((x) => toSnakeCase(x));
    }
    else if (t != null && t.constructor === Object) {
        return Object.fromEntries(Object.entries(t).map(([key, val]) => {
            const convVal = typeof val === 'object' ? toSnakeCase(val) : val;
            return [camelToSnake(key), convVal];
        }));
    }
    return t;
};
export const toDatabaseQuery = (obj, stringifyArrays = true) => {
    const newObj = {};
    for (const key in obj) {
        const newKey = camelToSnake(key);
        if (stringifyArrays && obj[key] && Array.isArray(obj[key])) {
            newObj[newKey] = JSON.stringify(obj[key]);
        }
        else {
            newObj[newKey] = obj[key];
        }
    }
    return newObj;
};
export const fromDatabaseQuery = (obj) => {
    const newObj = {};
    for (const key in obj) {
        const newKey = snakeToCamel(key);
        newObj[newKey] = obj[key];
    }
    return newObj;
};
export const setIntersection = (a, b) => new Set([...a].filter((i) => b.has(i)));
export const assertUnreachable = (x, error) => {
    throw new Error(error ?? `Should be Unreachable ${JSON.stringify(x)}`);
};
export const entries = (a) => {
    return Object.entries(a);
};
export const entries2 = (a) => {
    return Object.entries(a);
};
export const fromEntries = (a) => {
    return Object.fromEntries(a);
};
export const typeSafeObjectFromEntries = (entries) => {
    return Object.fromEntries(entries);
};
export const batchArray = (arr, size) => {
    const newArr = [];
    const rounds = Math.ceil(arr.length / size);
    for (let i = 0; i < rounds; i++) {
        newArr.push(arr.slice(i * size, i * size + size));
    }
    return newArr;
};
export const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
};
export const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
export const omit = (obj, keys) => {
    return Object.fromEntries(entries(obj).filter(([key]) => !keys.includes(key)));
};
export const throttle = (fn, options = { maxConcurrent: 5 }) => {
    const queue = [];
    let current = 0;
    const execute = (fn, ...args) => {
        return fn(...args)
            .then((r) => {
            if (queue.length === 0) {
                current -= 1;
            }
            else {
                queue.shift()();
            }
            return r;
        })
            .catch((err) => {
            if (queue.length === 0) {
                current -= 1;
            }
            else {
                queue.shift()();
            }
            throw err;
        });
    };
    return (...args) => {
        if (current < options.maxConcurrent) {
            current += 1;
            return execute(fn, ...args);
        }
        let resolve = (value) => { };
        const called = new Promise((r) => {
            resolve = r;
        });
        queue.push(resolve);
        const next = async () => {
            await called;
            return await execute(fn, ...args);
        };
        return next();
    };
};
export const extractError = (err) => {
    if (err instanceof Error) {
        return err.message;
    }
    else if (typeof err === 'string') {
        return err;
    }
    try {
        return JSON.stringify(err);
    }
    catch (err) {
        console.error(err);
        return '';
    }
};
export const wait = (ms) => new Promise((resolve, reject) => {
    setTimeout(resolve, ms);
});
/*type Value =
  | string
  | number
  | { [key: string]: Value }
  | Value[]
  | null
  | boolean
  | undefined*/
export const genObjectKey = (value) => {
    if (Array.isArray(value)) {
        return `[${value.map((v) => genObjectKey(v)).join(',')}]`;
    }
    if (typeof value === 'object') {
        if (value === null) {
            return `null`;
        }
        let agg = '';
        const keys = Object.keys(value).sort();
        for (const key of keys) {
            // @ts-expect-error
            const val = genObjectKey(value[key]);
            agg += `${key}: ${val},`;
        }
        return `{${agg}}`;
    }
    if (typeof value === 'string') {
        return `"${value}"`;
    }
    if (typeof value === 'undefined') {
        return `undefined`;
    }
    if (typeof value === 'number') {
        return `${value}`;
    }
    if (typeof value === 'boolean') {
        return `${value}`;
    }
    return '';
};
export const parseOrStr = (str) => {
    try {
        return JSON.parse(str);
    }
    catch (err) {
        return str;
    }
};
export const toggleInSet = (set, value) => {
    const next = new Set(set);
    if (next.has(value)) {
        next.delete(value);
    }
    else {
        next.add(value);
    }
    return next;
};
export const randomToken = (length = 8) => {
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += letters[Math.floor(Math.random() * letters.length)];
    }
    return result;
};
//# sourceMappingURL=index.js.map